<div class="relative h-full w-full flex flex-col gap-y-4 overflow-hidden">
    @if (busy()) {
    <atom-loading-overlay />
    }

    <!-- Search + create -->
    <header class="flex flex-col gap-y-3 w-full pb-3 border-b border-gray-200 dark:border-white/10">
        <div class="flex items-start justify-between gap-x-4">
            <div>
                <h3 class="text-base font-semibold text-gray-900 dark:text-white">Your groups</h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    @if (mode() === 'add-creators') {
                    Choose the group these creators should join.
                    } @else if (mode() === 'attach-to-brief') {
                    Choose the group of creators for this brief.
                    } @else {
                    Choose a group.
                    }
                </p>
            </div>
            @if (allowCreate()) {
            <button type="button" (click)="toggleCreateForm()"
                class="shrink-0 rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:shadow-none dark:inset-ring-white/5 dark:hover:bg-white/20">
                {{ showCreateForm() ? 'Cancel' : 'New group' }}
            </button>
            }
        </div>

        @if (showCreateForm()) {
        <div class="flex flex-col gap-y-2 rounded-md bg-gray-50 dark:bg-white/5 p-3">
            <input [formField]="createForm.groupName" id="group-picker-new-name" type="text"
                placeholder="Group name" class="input text-primary" />
            <input [formField]="createForm.groupDescription" id="group-picker-new-description" type="text"
                placeholder="Description (optional)" class="input text-primary" />
            <button type="button" (click)="createGroup()"
                class="self-start rounded-full bg-accent px-3 py-1 text-xs font-semibold text-white hover:opacity-90">
                Create group
            </button>
        </div>
        }

        <div class="grid grid-cols-1">
            <input [formField]="searchQueryForm.query" id="group-picker-search" type="text"
                placeholder="Search groups" class="input-icon text-primary" />
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-500 sm:size-4">
                <path d="m21 21-4.34-4.34" />
                <circle cx="11" cy="11" r="8" />
            </svg>
        </div>
    </header>

    <!-- Groups -->
    <main class="h-full flex-1 overflow-y-auto scrollbar-transparent">
        @if (displayGroups().length > 0) {
        <ul role="list" class="divide-y divide-gray-200 dark:divide-white/5">
            @for (group of displayGroups(); track group.id) {
            <li (click)="selectGroup(group)"
                class="flex items-start justify-between gap-x-6 py-3 px-2 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer transition-colors rounded-md"
                [class]="selectedGroupId() === group.id ? 'bg-primary/10 dark:bg-primary/15' : ''">
                <div class="min-w-0 flex-auto">
                    <div class="flex items-center gap-x-2">
                        <p class="text-sm/6 font-semibold text-gray-900 dark:text-white truncate">{{ group.groupName }}</p>
                        @if (isLocked(group)) {
                        <span title="Synced with SHOUT AI — creators can be added but not removed"
                            class="shrink-0 inline-flex items-center gap-x-1 rounded-full bg-gray-100 dark:bg-white/10 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:text-gray-300">
                            &#8635; Synced
                        </span>
                        }
                    </div>
                    @if (group.groupDescription) {
                    <p class="mt-0.5 truncate text-xs/5 text-gray-500 dark:text-gray-400">{{ group.groupDescription }}</p>
                    }
                    <p class="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                        {{ group.numberOfCreators ?? 0 }} creator{{ (group.numberOfCreators ?? 0) === 1 ? '' : 's' }}
                    </p>
                    @for (brief of usageBriefs(group); track brief.briefId) {
                    <p class="mt-0.5 text-xs text-gray-400 dark:text-gray-500 truncate">
                        &#8627; {{ brief.kind }}: "{{ brief.briefTitle }}"
                    </p>
                    }
                </div>
                @if (selectedGroupId() === group.id) {
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    class="size-5 shrink-0 text-accent mt-1">
                    <path d="M20 6 9 17l-5-5" />
                </svg>
                }
            </li>
            }
        </ul>
        } @else if (loaded()) {
        <div class="flex flex-col items-center justify-center w-full h-full gap-y-2 py-8">
            <p class="text-sm text-gray-500 dark:text-gray-400">No groups found.</p>
            @if (allowCreate()) {
            <button type="button" (click)="toggleCreateForm()"
                class="text-sm font-semibold text-accent hover:underline">Create a group</button>
            }
        </div>
        } @else {
        <div class="flex flex-col items-center justify-center w-full h-full py-8">
            <p class="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        </div>
        }
    </main>

    <!-- Confirm -->
    @if (selectedGroup() && mode() !== 'browse') {
    <footer class="flex flex-col gap-y-2 pt-3 border-t border-gray-200 dark:border-white/10">
        @if (fanOutWarning()) {
        <p class="text-xs text-gray-500 dark:text-gray-400">{{ fanOutWarning() }}</p>
        }
        <button type="button" (click)="confirm()" [disabled]="busy()"
            class="w-full rounded-full bg-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
            {{ confirmLabel() }}
        </button>
    </footer>
    }
</div>
